import Sugar from 'sugar';
import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { Debouncer } from '../../../../../ltn-helpers/utils/debouncer.js';
import { timeOut } from '../../../../../ltn-helpers/utils/async.js';

import { callLambda, getActionFromLambdaResults } from '../../../../../helpers/lambda.js';
import { viewPath } from '../../../../../helpers/route.js';

import NSJobComponent from '../../../job.js';

import JobSchema from '../../../../../models/job-schema.js';
import Company from '../../../../../models/company.js';

import type { Option } from '../../../../../ns-elements/ns-multi-select.js';
import '../../../../../ns-elements/ns-multi-select.js';
import '../../../../../ns-elements/ns-depth-input.js';
import '../../../../../ns-elements/ns-relationship-summary.js';
import type { RelationshipSummaryParty } from '../../../../../ns-elements/ns-relationship-summary.js';
import { renderFormField } from '../../../../../helpers/form-field.js';
import { toDateInputValue } from '../../../../../helpers/format.js';
import { depthChoiceLabel } from '../../../../../ns-relationship/relationship-terms.js';
import {
  emptyInvitee,
  inviteeErrors,
  inviteePayload,
  requestProblem,
  type InviteeDraft,
  type InviteeDraftField,
} from '../../../../../ns-relationship/connect-invitees.js';
import '../../../../../ns-elements/ns-job-layout.js';
import { nsFormFieldStyles } from '../../../../../ns-styles/index.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/connect/request.json';
import { jobSchema } from '../../../job-schema.js';

const NOTES_MAX_LENGTH = 2000;
const TITLE_MAX_LENGTH = 150;
const INVITEE_FIELDS: { field: InviteeDraftField; label: string; type?: string; hint?: string }[] = [
  { field: 'companyName', label: 'Company name' },
  { field: 'registrarIdentifier', label: 'Company number', hint: 'Optional. Their Companies House number.' },
  { field: 'name', label: 'Contact name' },
  { field: 'jobTitle', label: 'Job title' },
  { field: 'email', label: 'Email', type: 'email' },
];

@customElement(NSCompanyConnectRequest.is)
export default class NSCompanyConnectRequest extends NSJobComponent {
  static get is() {
    return 'ns-company-connect-request';
  }

  private __debounceSearchInput?: Debouncer;

  @state()
  private __baseCompany?: Company;

  @state()
  private __searchResults: Option[] = [];

  @state()
  private __searchStatus = '';

  @state()
  private __selected: Option[] = [];

  private __companiesById = new Map<string, Company>();

  @state()
  private __title = '';

  @state()
  private __notes = '';

  @state()
  private __contractValue?: number;

  @state()
  private __contractCurrency = 'GBP';

  @state()
  private __reviewDate?: string = toDateInputValue(Sugar.Date.create('in 6 months'));

  @state()
  private __requestedDepth = 0;

  @state()
  private __counterpartiesError = '';

  @state()
  private __notesError = '';

  @state()
  private __invitees: InviteeDraft[] = [];

  // Field errors show only after a send, then follow each edit
  @state()
  private __inviteesChecked = false;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { submit: { type: 'hidden' }, cancel: { type: 'hidden' } } });
  }

  static styles = [
    ...super.styles,
    nsFormFieldStyles,
    css`
      textarea {
        width: 100%;
        min-height: 96px;
        resize: vertical;
        box-sizing: border-box;
        background-color: var(--primary-input-background-color);
        color: var(--primary-input-text-color);
        border: none;
        outline: none;
        padding: var(--wa-space-xs) var(--wa-space-s);
        border-radius: var(--wa-border-radius-l);
        font-size: 1em;
        font-family: inherit;
      }

      textarea:focus-visible {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: 2px;
      }

      .invitee {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-xs);
        padding: var(--wa-space-s) var(--wa-space-m);
        border-radius: var(--wa-border-radius-l);
        background: var(--wa-color-surface-lowered);
      }

      .invitee__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .invitee__header h4 {
        margin: 0;
      }

      .invitee__fields {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(12em, 1fr));
        gap: var(--wa-space-xs) var(--wa-space-m);
      }

      ns-relationship-summary {
        border-radius: var(--ns-card-border-radius, 0.5em);
        background: var(--wa-color-surface-lowered);
      }
    `,
  ];

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      const baseId = this.job.inputValues.BASE_COMPANY_ID;
      if (!baseId || typeof baseId !== 'string') throw new Error('Missing required base company ID');

      this.__baseCompany = await this._db.getById<Company>('company', baseId);
      if (!this.__baseCompany) throw new Error(`Unable to find company with id ${baseId}`);

      // Optional launcher input, set by Connect callers that already know who to invite.
      const preselectedId = this.job.inputValues.PRESELECTED_CONNECTOR_ID;
      if (preselectedId && typeof preselectedId === 'string') {
        const preselected = await this._db.getById<Company>('company', preselectedId);
        if (preselected) {
          this.__companiesById.set(preselected.id, preselected);
          this.__selected = [{ value: preselected.id, label: preselected.name }];
        }
      }
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the company');
    }
  }

  private __onCompanySearchInput(evt: CustomEvent<{ query: string }>) {
    const val = evt.detail.query;
    if (!val || val.length < 3) {
      this.__searchResults = [];
      this.__searchStatus = '';
      return;
    }

    this.__debounceSearchInput = Debouncer.debounce(this.__debounceSearchInput, timeOut.after(10), async () => {
      if (!this._db) throw new Error('Unable to find db service');
      if (!this.__baseCompany?.id) throw new Error('Missing required base company');

      this.__searchStatus = 'Searching for companies...';

      const queryCall = await this._db.query('company', { name: { $rexi: val } }, { limit: 10 });
      const results = (queryCall.results as unknown as Company[]).filter((c) => c.id !== this.__baseCompany?.id);

      results.forEach((company) => this.__companiesById.set(company.id, company));
      this.__searchResults = results.map((c) => ({ value: c.id, label: c.name }));
      this.__searchStatus =
        results.length === 0 ? "We couldn't find any companies in the system using that search term." : '';
    });
  }

  private __onSelectionChanged(evt: CustomEvent<{ selected: Option[] }>) {
    this.__selected = evt.detail.selected;
    this.__counterpartiesError = '';
  }

  private __addInvitee() {
    this.__invitees = [...this.__invitees, emptyInvitee()];
    this.__counterpartiesError = '';
  }

  private __removeInvitee(index: number) {
    this.__invitees = this.__invitees.filter((_, i) => i !== index);
  }

  private __onInviteeInput(index: number, field: InviteeDraftField, evt: InputEvent) {
    const value = (evt.target as HTMLInputElement).value;
    this.__invitees = this.__invitees.map((invitee, i) => (i === index ? { ...invitee, [field]: value } : invitee));
    this.__counterpartiesError = '';
  }

  private __onTitleInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__title = target.value.slice(0, TITLE_MAX_LENGTH);
  }

  private __onNotesInput(evt: InputEvent) {
    const target = evt.target as HTMLTextAreaElement;
    this.__notes = target.value.slice(0, NOTES_MAX_LENGTH);
    this.__notesError = '';
  }

  private __onContractValueInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__contractValue = target.value ? Number(target.value) : undefined;
  }

  private __onReviewDateInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__reviewDate = target.value || undefined;
  }

  private async __sendRequest() {
    this.__counterpartiesError = '';
    this.__notesError = '';

    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__baseCompany?.id) throw new Error('Missing required base company');

    this.__inviteesChecked = true;
    if (this.__invitees.some((invitee) => Object.keys(inviteeErrors(invitee)).length > 0)) {
      this.fatalError('Please check the details of the companies you are inviting by email.');
      return;
    }
    if (!this.__notes.trim()) {
      this.__notesError = 'Please add a note describing this connection.';
      this.fatalError(this.__notesError);
      return;
    }
    const problem = requestProblem(this.__selected.length, this.__invitees, this.__notes);
    if (problem) {
      this.__counterpartiesError = problem;
      this.fatalError(problem);
      return;
    }

    try {
      this.loading('Sending Request');
      const lambdaResult = await callLambda(
        'company/connect-create',
        'POST',
        this._auth.token,
        JSON.stringify({
          base: this.__baseCompany.id,
          counterparties: this.__selected.map((option) => option.value),
          invitees: this.__invitees.map(inviteePayload),
          oauthApp: this.__invitees.length ? (sessionStorage.getItem('oauthApp') ?? undefined) : undefined,
          description: this.__title.trim() || undefined,
          notes: this.__notes,
          contractValue: this.__contractValue,
          contractCurrency: this.__contractCurrency,
          reviewDate: this.__reviewDate,
          requestedDepth: this.__requestedDepth,
          requestJobId: this._job?.id,
        }),
      );

      const created = await getActionFromLambdaResults(lambdaResult, 'commercial_relationship_created');
      this.loaded();
      await this.outcome('submit', { notes: this.__notes });
      if (typeof created !== 'string') viewPath(`/relationships/${created.id}`);
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while sending the request');
    }
  }

  private async __cancel() {
    this.outcome('cancel');
  }

  private __renderForm() {
    return html`
      <div class="job-panel">
        <h3>Search, invite, and tell them why</h3>
        <p>
          Search for the companies you'd like to invite as suppliers, add a short note explaining the relationship, and
          optionally record a contract value and review date. Each invited company is notified and can accept or
          decline. A company that isn't on nodeStream yet can be invited by email, and joins once it signs up.
        </p>
        ${renderFormField({
          id: 'title',
          label: 'Title',
          hint: 'Optional. Leave it blank to use the one shown.',
          control: html`
            <wa-input
              class="w-100"
              id="title"
              type="text"
              maxlength=${TITLE_MAX_LENGTH}
              placeholder=${this.__autoTitle()}
              .value=${this.__title}
              @input=${this.__onTitleInput}
            ></wa-input>
          `,
        })}
        ${renderFormField({
          id: 'company-search',
          label: 'Search for companies to invite as suppliers',
          error: this.__counterpartiesError,
          control: html`
            <ns-multi-select
              id="company-search"
              label="Search for companies to invite as suppliers"
              placeholder="Company Name"
              .options=${this.__searchResults}
              .selected=${this.__selected}
              .searchStatus=${this.__searchStatus}
              @search-input=${this.__onCompanySearchInput}
              @selection-changed=${this.__onSelectionChanged}
            ></ns-multi-select>
          `,
        })}
        ${this.__invitees.map((invitee, index) => this.__renderInvitee(invitee, index))}
        <div>
          <wa-button appearance="outlined" variant="brand" type="button" @click=${this.__addInvitee}>
            Not on nodeStream? Invite them by email
          </wa-button>
        </div>
        ${renderFormField({
          id: 'notes',
          label: 'Why do you want to connect? (required)',
          error: this.__notesError,
          control: html`
            <textarea
              id="notes"
              maxlength=${NOTES_MAX_LENGTH}
              .value=${this.__notes}
              aria-required="true"
              aria-invalid=${this.__notesError ? 'true' : 'false'}
              aria-describedby="notes-error"
              @input=${this.__onNotesInput}
            ></textarea>
          `,
        })}
        <ns-depth-input
          .value=${this.__requestedDepth}
          @depth-change=${(e: CustomEvent<{ value: number }>) => (this.__requestedDepth = e.detail.value)}
        >
          Applies to every company invited above.
        </ns-depth-input>
        <div class="field-row">
          ${renderFormField({
            id: 'contractValue',
            label: 'Contract value',
            control: html`
              <wa-input class="w-100" id="contractValue" type="number" min="0" @input=${this.__onContractValueInput}>
                <span slot="start" aria-hidden="true">£</span>
              </wa-input>
            `,
          })}
          ${renderFormField({
            id: 'reviewDate',
            label: 'Review date',
            hint: 'Six months from today unless you change it.',
            control: html`
              <input
                class="w-100"
                id="reviewDate"
                type="date"
                .value=${this.__reviewDate ?? ''}
                @input=${this.__onReviewDateInput}
              />
            `,
          })}
        </div>
      </div>
    `;
  }

  private __renderInvitee(invitee: InviteeDraft, index: number) {
    const errors = this.__inviteesChecked ? inviteeErrors(invitee) : {};
    return html`
      <div class="invitee" role="group" aria-label=${`Company invited by email ${index + 1}`}>
        <div class="invitee__header">
          <h4>Invited by email</h4>
          <wa-button appearance="plain" size="small" type="button" @click=${() => this.__removeInvitee(index)}>
            Remove
          </wa-button>
        </div>
        <div class="invitee__fields">
          ${INVITEE_FIELDS.map(({ field, label, type, hint }) => {
            const id = `invitee-${index}-${field}`;
            return renderFormField({
              id,
              label,
              hint,
              error: errors[field],
              control: html`
                <wa-input
                  class="w-100"
                  id=${id}
                  type=${type ?? 'text'}
                  .value=${invitee[field]}
                  aria-invalid=${errors[field] ? 'true' : 'false'}
                  aria-describedby=${`${id}-error`}
                  @input=${(evt: InputEvent) => this.__onInviteeInput(index, field, evt)}
                ></wa-input>
              `,
            });
          })}
        </div>
      </div>
    `;
  }

  private __invitedCompanyNames() {
    return this.__invitees.map((invitee) => invitee.companyName.trim()).filter(Boolean);
  }

  private __autoTitle() {
    const invited = [...this.__selected.map((option) => option.label), ...this.__invitedCompanyNames()];
    return `${this.__baseCompany?.name ?? 'Your company'} <-> ${invited.length ? invited.join(', ') : 'Supplier name'}`;
  }

  private __summaryParties(): RelationshipSummaryParty[] {
    const invited = this.__selected
      .map((option) => this.__companiesById.get(option.value))
      .filter((company): company is Company => !!company)
      .map((company) => ({ company, role: 'Supplier' }));
    return this.__baseCompany ? [{ company: this.__baseCompany, role: 'Client (you)' }, ...invited] : invited;
  }

  render() {
    return html`
      <ns-job-layout>
        <p slot="intro">
          Connecting creates a commercial relationship with you as the client and the companies you invite as your
          suppliers — the basis for sharing data, requesting deeper network access, and collaborating through shared
          workflows.
        </p>
        ${this.__renderForm()}
        <ns-relationship-summary
          slot="aside"
          heading="The relationship you're proposing"
          .parties=${this.__summaryParties()}
          .invitedByEmail=${this.__invitedCompanyNames()}
          .contractValue=${this.__contractValue}
          contractCurrency=${this.__contractCurrency}
          .reviewDate=${this.__reviewDate ?? Sugar.Date.create('in 6 months')}
          .requestedDepth=${this.__requestedDepth}
          depthLabel=${`${depthChoiceLabel(this.__requestedDepth)}${this.__requestedDepth ? ', if they share' : ''}`}
        ></ns-relationship-summary>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__cancel()}>Cancel</wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__sendRequest()}>Send Request</wa-button>
    `;
  }
}
